/**
 * A clinic's booking page on its own host: `cathys.clarohealth.app`
 * (CLA-2010).
 *
 * The `claro-booking-host` Worker (ops `terraform/cloudflare/workers/
 * booking_host.js`) serves the clinic's realm's `/book/<slug>` shell at `/`,
 * and adds two `<meta>` tags: the slug, and the realm's API origin. On such a
 * host the SPA:
 *
 *  - renders only the Book page (`/`) and its embed (`/embed`), for the slug
 *    in the host name;
 *  - calls the public booking API on that API origin directly
 *    (`bookingApiUrl`), never through the Worker, so a patient's details go
 *    straight to their clinic's realm (and US ones never touch Cloudflare,
 *    ADR-038). The backend allows these origins on `/public/*` only, without
 *    credentials (`ClaroWeb.Plugs.BookingHostCors`).
 *
 * Anywhere else (claro.clinic, staging, a dev server) every function here is
 * inert and the Book page works exactly as before at `/book/<slug>`.
 */

export const BOOKING_HOST_DOMAIN = 'clarohealth.app'

/**
 * The only API origins a booking host may be pointed at: each realm's own
 * (the hosts the mobile apps pin, `api-url.ts` `APP_API_ORIGIN`). A meta tag
 * naming anything else is ignored.
 */
const BOOKING_API_ORIGINS = new Set(['https://api-au.claro.clinic', 'https://api.us.claro.clinic'])

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

type Loc = Pick<Location, 'hostname'>

/**
 * The clinic slug when this page is a clinic's booking host
 * (`<slug>.clarohealth.app`, one label), else null.
 */
export function bookingHostSlug(loc: Loc = location): string | null {
  const suffix = `.${BOOKING_HOST_DOMAIN}`
  if (!loc.hostname.endsWith(suffix)) return null
  const label = loc.hostname.slice(0, -suffix.length)
  return SLUG_RE.test(label) && label.length >= 3 && label.length <= 40 ? label : null
}

export function onBookingHost(loc: Loc = location): boolean {
  return bookingHostSlug(loc) != null
}

function meta(name: string, doc: Document = document): string | null {
  return doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null
}

/** The realm API origin the Worker named for this booking host, or null. */
export function bookingApiOrigin(loc: Loc = location, doc: Document = document): string | null {
  if (!onBookingHost(loc)) return null
  const origin = meta('claro-booking-api', doc)
  return origin && BOOKING_API_ORIGINS.has(origin) ? origin : null
}

/**
 * Absolute URL for a public booking path (`/public/...`) when on a booking
 * host; the path unchanged everywhere else.
 */
export function bookingApiUrl(path: string, loc: Loc = location, doc: Document = document): string {
  const origin = bookingApiOrigin(loc, doc)
  return origin && path.startsWith('/') ? `${origin}${path}` : path
}

/**
 * An image or file the API serves by a relative path (a practitioner's
 * photo, `/public/practitioners/:id/photo`), made absolute on a booking host.
 */
export function bookingAssetUrl(url: string | null | undefined): string | null {
  if (!url) return null
  return url.startsWith('/') && !url.startsWith('//') ? bookingApiUrl(url) : url
}

/**
 * A link to a page of the app that lives on claro.clinic (portal sign-in),
 * from a booking host: absolute, realm-pinned by the Worker's own
 * `claro_realm` rule. Unchanged off a booking host.
 */
export function appPageUrl(path: string, loc: Loc = location, doc: Document = document): string {
  if (!onBookingHost(loc)) return path
  const realm = meta('claro-booking-realm', doc)
  const url = new URL(path, 'https://claro.clinic')
  if (realm === 'au' || realm === 'us') url.searchParams.set('claro_realm', realm)
  return url.toString()
}

/**
 * A server-sent booking-page address (`booking_url`, `rebook_url`) when it is
 * a clinic's own host, else null: the caller keeps its in-app `/book/...`
 * link, which the old servers and every non-production host still use.
 */
export function bookingHostUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && bookingHostSlug(u) != null ? u.toString().replace(/\/$/, '') : null
  } catch {
    return null
  }
}

/** The clinic's address choice (CLA-2018). Unknown values from a newer server read as `host`. */
export type BookingAddress = 'host' | 'plain'

/** What `GET /manage/clinics/:id` says about the clinic's own host (CLA-2010). */
export interface BookingHostInfo {
  /** `clarohealth.app` when this deployment publishes hosts, else null. */
  domain: string | null
  /** The clinic's links already use its host. */
  live: boolean
  /** The clinic's choice; absent from a server before CLA-2018. */
  address?: string
}

/** Whether the panel should check back for the host going live (it takes the server a moment). */
export function bookingHostPending(info: BookingHostInfo | null | undefined, enabled: boolean | undefined): boolean {
  return !!info?.domain && !info.live && (info.address ?? 'host') === 'host' && enabled === true
}
